// src/components/Products/ImportDialog.jsx
import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  Button,
  Chip,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";

// Cabeçalhos exibidos na prévia/ajuda
const HEADERS = [
  "id",
  "sku",
  "nome",
  "tipo",
  "preco",
  "preco_comdesconto",
  "estoque",
  "status",
  "detalhes",
  "descricao_ia",
  "id_categoria",
  "id_subcategoria",
  "categoria_nome",
  "subcategoria_nome",
  "peso",
  "largura",
  "altura",
  "comprimento",
  "aplicacao_serie_truck",
  "aplicacao_serie_bus",
  "aplicacao_motores",
  "inclusos_itens",
  "compat_alerta",
  "garantia_alerta",
  "garantia_descricao",
  "garantia_policy_url",
  "trocas_alerta",
  "trocas_descricao",
  "trocas_col_esq",
  "trocas_col_dir",
  "pagamento_regras",
  "imagens_urls",
];

const cell = { padding: 6, verticalAlign: "top", borderBottom: "1px solid #eee" };
const ellipsis = {
  ...cell,
  maxWidth: 280,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const ImportDialog = ({
  open,
  rows,
  applying,
  onClose,
  onApply,
  onDownloadTemplate,
}) => {
  const { t } = useTranslation("products");
  const statusMeta = useMemo(
    () => ({
      new: {
        label: t("importDialog.create"),
        color: "#e8f5e9",
        chipColor: "success",
      },
      update: {
        label: t("importDialog.update"),
        color: "#fff8e1",
        chipColor: "warning",
      },
      error: {
        label: t("importDialog.errorRow"),
        color: "#ffebee",
        chipColor: "error",
      },
    }),
    [t]
  );
  const total = rows.length || 0;
  const stats = rows.reduce(
    (acc, r) => {
      const st = r.__status || "new";
      acc[st] = (acc[st] || 0) + 1;
      return acc;
    },
    { new: 0, update: 0, error: 0 }
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t("importDialog.title")}</DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("importDialog.infoIntro", { headers: HEADERS.join(", ") })}
        </Alert>

        {total > 0 && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Typography variant="body2" sx={{ mr: 1 }}>
              {t("importDialog.preValidation")}
            </Typography>
            <Chip
              size="small"
              label={`${t("importDialog.create")}: ${stats.new || 0}`}
              sx={{ bgcolor: statusMeta.new.color }}
            />
            <Chip
              size="small"
              label={`${t("importDialog.update")}: ${stats.update || 0}`}
              sx={{ bgcolor: statusMeta.update.color }}
            />
            <Chip
              size="small"
              label={`${t("importDialog.errors")}: ${stats.error || 0}`}
              sx={{ bgcolor: statusMeta.error.color }}
            />
            <Typography variant="caption" sx={{ ml: "auto", opacity: 0.7 }}>
              {t("importDialog.redRowsHint")}
            </Typography>
          </Box>
        )}

        <Box sx={{ maxHeight: 500, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th
                  style={{
                    borderBottom: "1px solid #ddd",
                    textAlign: "left",
                    padding: "6px",
                    position: "sticky",
                    top: 0,
                    background: "var(--mui-palette-background-paper)",
                    whiteSpace: "nowrap",
                    zIndex: 1,
                  }}
                >
                  {t("importDialog.actionCol")}
                </th>
                {HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      borderBottom: "1px solid #ddd",
                      textAlign: "left",
                      padding: "6px",
                      position: "sticky",
                      top: 0,
                      background: "var(--mui-palette-background-paper)",
                      whiteSpace: "nowrap",
                      zIndex: 1,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => {
                const st = r.__status || "new";
                const meta = statusMeta[st] || statusMeta.new;
                const errors = Array.isArray(r.__errors)
                  ? r.__errors.filter(Boolean)
                  : [];

                return (
                  <tr key={i} style={{ backgroundColor: meta.color }}>
                    {/* Ação + erros */}
                    <td style={{ ...cell, minWidth: 140 }}>
                      <Chip
                        size="small"
                        label={meta.label}
                        color={meta.chipColor}
                        variant="outlined"
                        sx={{ mb: errors.length ? 0.5 : 0 }}
                      />
                      {errors.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "#b71c1c",
                            mt: 0.5,
                          }}
                        >
                          {errors.join(" • ")}
                        </Typography>
                      )}
                    </td>

                    <td style={cell}>{r.id ?? ""}</td>
                    <td style={cell}>{r.sku ?? ""}</td>
                    <td style={cell}>{r.nome}</td>
                    <td style={cell}>{r.tipo ?? ""}</td>

                    <td style={cell}>{r.preco ?? ""}</td>
                    <td style={cell}>{r.preco_comdesconto ?? ""}</td>
                    <td style={cell}>{r.estoque ?? ""}</td>
                    <td style={cell}>{r.status ?? 1}</td>

                    <td style={ellipsis}>{r.detalhes ?? ""}</td>
                    <td style={ellipsis}>{r.descricao_ia ?? ""}</td>

                    <td style={cell}>{r.id_categoria ?? ""}</td>
                    <td style={cell}>{r.id_subcategoria ?? ""}</td>
                    <td style={cell}>{r.categoria_nome ?? ""}</td>
                    <td style={cell}>{r.subcategoria_nome ?? ""}</td>

                    <td style={cell}>{r.peso ?? ""}</td>
                    <td style={cell}>{r.largura ?? ""}</td>
                    <td style={cell}>{r.altura ?? ""}</td>
                    <td style={cell}>{r.comprimento ?? ""}</td>

                    <td style={ellipsis}>
                      {(r.aplicacao_serie_truck || []).join?.(", ") ||
                        r.aplicacao_serie_truck ||
                        ""}
                    </td>
                    <td style={ellipsis}>
                      {(r.aplicacao_serie_bus || []).join?.(", ") ||
                        r.aplicacao_serie_bus ||
                        ""}
                    </td>
                    <td style={ellipsis}>
                      {(r.aplicacao_motores || []).join?.(", ") ||
                        r.aplicacao_motores ||
                        ""}
                    </td>

                    <td style={ellipsis}>{r.inclusos_itens ?? ""}</td>
                    <td style={ellipsis}>{r.compat_alerta ?? ""}</td>
                    <td style={ellipsis}>{r.garantia_alerta ?? ""}</td>
                    <td style={ellipsis}>{r.garantia_descricao ?? ""}</td>
                    <td style={ellipsis}>{r.garantia_policy_url ?? ""}</td>
                    <td style={ellipsis}>{r.trocas_alerta ?? ""}</td>
                    <td style={ellipsis}>{r.trocas_descricao ?? ""}</td>
                    <td style={ellipsis}>{r.trocas_col_esq ?? ""}</td>
                    <td style={ellipsis}>{r.trocas_col_dir ?? ""}</td>
                    <td style={ellipsis}>{r.pagamento_regras ?? ""}</td>

                    <td style={ellipsis}>{r.imagens_urls ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseRoundedIcon />}>
          {t("importDialog.cancel")}
        </Button>
        <Button onClick={onDownloadTemplate}>{t("importDialog.downloadTemplate")}</Button>
        <Button
          variant="contained"
          onClick={onApply}
          disabled={applying || !rows.length}
        >
          {applying ? t("importDialog.applying") : t("importDialog.applyImport")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
